import { Fragment, useState } from "react";

import {
  faCheck,
  faChevronDown,
  faCircleNotch,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import {
  computeToolCallStatus,
  ToolCallAccordionItem,
  type ToolCallStatus,
  type ToolCallWithResult,
} from "src/base-components/agents/ToolCallDisplay";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { usePowertools } from "src/powertools/hooks";

export const ToolCallCompactRow: React.FC<{
  toolCall: ToolCallWithResult["toolCall"];
  status: ToolCallStatus | null;
}> = ({ toolCall, status }) => (
  <div className="flex items-center justify-between rounded-lg border border-gray-200 px-2 py-1.5">
    <span className="min-w-0 truncate font-inter-medium-12px text-gray-800">
      {toolCall.metadata.display_name}
    </span>
    {status && (
      <Pill size="sm" variant={status === "error" ? "red" : "green"}>
        <Pill.Icon icon={status === "error" ? faWarning : faCheck} />
        <Pill.Text>{status === "error" ? "Failed" : "Successful"}</Pill.Text>
      </Pill>
    )}
  </div>
);

export type ToolCallGroupItem =
  | ({ kind: "tool" } & ToolCallWithResult)
  | { kind: "text"; key: string; text: string };

export const ToolCallGroup: React.FC<{
  items: ToolCallGroupItem[];
  sessionSubstatus?: string | null;
  isInProgress?: boolean;
  alwaysShowToolDetails?: boolean;
  showDescription?: boolean;
  showTokenUsage?: boolean;
  dataLoc?: string;
  /** Overrides how text items render */
  renderText?: (text: string) => React.ReactNode;
}> = ({
  items,
  sessionSubstatus = null,
  isInProgress = false,
  alwaysShowToolDetails = false,
  showDescription = true,
  showTokenUsage = true,
  dataLoc,
  renderText,
}) => {
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? isInProgress;
  const isPowerToolsEnabled = usePowertools();
  const showToolDetails = alwaysShowToolDetails || isPowerToolsEnabled;
  const toolCalls = items.filter((item) => item.kind === "tool");
  const count = toolCalls.length;
  const isRunning = toolCalls.some(({ toolResult }) => {
    const status = computeToolCallStatus(toolResult, sessionSubstatus);
    return status === "running" || status === "paused";
  });

  return (
    <div data-loc={dataLoc}>
      <button
        className="flex items-center gap-x-1 font-inter-normal-12px text-gray-400"
        type="button"
        onClick={() => setUserExpanded(!expanded)}
      >
        {isRunning ? (
          <>
            <Icon
              dataLoc={dataLoc && `${dataLoc}-spinner`}
              icon={faCircleNotch}
              size="2xs"
              spin
            />
            Running tools...
          </>
        ) : (
          `Called ${count} ${count === 1 ? "tool" : "tools"}`
        )}
        <div
          className={twJoin(
            "flex h-3 w-3 shrink-0 items-center justify-center transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        >
          <Icon icon={faChevronDown} size="2xs" />
        </div>
      </button>
      {expanded && (
        <div className="mt-1 space-y-2">
          {items.map((item) => {
            if (item.kind === "text") {
              return renderText ? (
                <Fragment key={item.key}>{renderText(item.text)}</Fragment>
              ) : (
                <ChatTextContent
                  key={item.key}
                  content={item.text}
                  delay={0}
                  useMarkdownRenderer
                />
              );
            }
            return showToolDetails ? (
              <ToolCallAccordionItem
                key={item.toolCall.id}
                showDescription={showDescription}
                showTokenUsage={showTokenUsage}
                status={computeToolCallStatus(
                  item.toolResult,
                  sessionSubstatus,
                )}
                toolCall={item.toolCall}
                toolResult={item.toolResult}
              />
            ) : (
              <ToolCallCompactRow
                key={item.toolCall.id}
                status={computeToolCallStatus(item.toolResult, null)}
                toolCall={item.toolCall}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
